import type { ReactNode, Ref } from 'react';

import { ThemedBlockContainerNew } from 'components/ThemedBlockContainer';
import { useIsInNestedPlaceholder } from 'contexts';
import type { ThemeName } from 'design-system/themes';
import type {
	ColorString,
	HTMLAttributes,
	HTMLBlockContainerTagName,
} from 'types';
import { cn } from 'utils/classNames';

import GridOverlay from './GridOverlay';
import { getContainerMaxWidthClass, type MaxWidth } from './helpers';

//TODO: Brittle api, Jens will think of something better!
export const GUTTER_CLASSES = 'px-r-md';
export const PULL_GUTTER_CLASSES = '-mx-r-md';
export const SM_GUTTER_CLASSES = 'sm:px-4 md:px-6';
export const SM_PULL_GUTTER_CLASSES = 'sm:-mx-4 md:-mx-6';
export const MD_GUTTER_CLASSES = 'md:px-6';
export const MD_PULL_GUTTER_CLASSES = 'md:-mx-6';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
	/**
	 * CSS color value, like hex.
	 *
	 * @deprecated Replaced by themes.
	 */
	backgroundColor?: ColorString;

	/** React children */
	children?: ReactNode;

	/** Include standard horizontal edge spacing classes */
	gutters?: 'md' | 'sm' | boolean;

	/**
	 * CSS color value, like hex.
	 *
	 * @deprecated Replaced by themes.
	 */
	innerBackgroundColor?: ColorString;

	/** Extra classes for the inner container */
	innerClassName?: string;

	/** Component to use for the inner element */
	innerComponent?: HTMLBlockContainerTagName;

	/** If nested inside other containers (disables max width and gutters) */
	isNested?: boolean;

	/** max-width for the container. */
	maxWidth?: MaxWidth;

	/** Extra classes for the outer container */
	outerClassName?: string;

	/** Show a grid overlay */
	overlay?: boolean;

	ref?: Ref<HTMLDivElement>;

	theme?: ThemeName;

	/** Set standard grid classes on the inner element. */
	withGrid?: boolean;

	/** Set vertical grid gap matching the horizontal one. */
	withVerticalGridGap?: boolean;
}

/** Layout container setting the page width. */
export default function LayoutContainer({
	backgroundColor,
	children,
	gutters = true,
	innerBackgroundColor,
	innerClassName = '',
	innerComponent: InnerComponent = 'div',
	isNested: isNestedProp = false,
	maxWidth = 'standard',
	outerClassName = '',
	overlay = false,
	ref,
	theme,
	withGrid = false,
	withVerticalGridGap = false,
	...outerAttrs
}: Props) {
	const isInNestedPlaceholder = useIsInNestedPlaceholder();
	const isNested = isNestedProp || isInNestedPlaceholder;

	const innerClasses = cn(
		!isNested && getContainerMaxWidthClass(maxWidth),
		'mx-auto',
		withGrid && 'grid grid-cols-4 gap-x-r-md md:grid-cols-12',
		withVerticalGridGap && 'gap-y-r-md',
		innerClassName,
	);

	return (
		<div
			ref={ref}
			style={backgroundColor ? { backgroundColor } : undefined}
			className={cn(
				outerClassName,
				backgroundColor && 'py-r-xl',
				gutters &&
					!isNested && [
						'print:px-0!',
						gutters === true && GUTTER_CLASSES,
						gutters === 'sm' && SM_GUTTER_CLASSES,
						gutters === 'md' && MD_GUTTER_CLASSES,
					],
				overlay && 'relative',
			)}
			{...outerAttrs}
		>
			{/* TODO: Setting background is deprecated, should only use themed container */}
			{!theme && (
				<InnerComponent
					style={
						innerBackgroundColor
							? { backgroundColor: innerBackgroundColor }
							: undefined
					}
					className={innerClasses}
				>
					{children}
				</InnerComponent>
			)}
			{theme && (
				<ThemedBlockContainerNew
					as={InnerComponent}
					theme={theme}
					className={innerClasses}
				>
					{children}
				</ThemedBlockContainerNew>
			)}
			{overlay && <GridOverlay maxWidth={maxWidth} />}
		</div>
	);
}

LayoutContainer.displayName = 'LayoutContainer';
